import { get } from 'aws-amplify/api';
import { isAuthError } from '../utils/api';
import pako from 'pako';

/**
 * Synchronous (main-thread) gzip-base64 decompress + parse.
 * Used as a fallback when a Web Worker is unavailable or errors out.
 * @param {string} compressedData - Base64 encoded gzipped data
 * @returns {any} Decompressed and parsed JSON data
 */
const decompressGzipBase64Sync = (compressedData) => {
  try {
    const binaryString = atob(compressedData);
    const uint8Array = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      uint8Array[i] = binaryString.charCodeAt(i);
    }
    const decompressed = pako.ungzip(uint8Array);
    const jsonString = new TextDecoder().decode(decompressed);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Decompression failed:', error);
    throw error;
  }
};

// Lazily-created Web Worker that handles heavy decompress + JSON.parse off the
// main thread so the UI (loading skeletons, animations) stays responsive.
let decompressWorker = null;
let workerRequestId = 0;
let workerUnavailable = false;
const workerPending = new Map();

const getDecompressWorker = () => {
  if (decompressWorker || workerUnavailable) return decompressWorker;
  try {
    decompressWorker = new Worker(
      new URL('../workers/treeDecompress.worker.js', import.meta.url)
    );
    decompressWorker.onmessage = (event) => {
      const { id, result, error } = event.data || {};
      const pending = workerPending.get(id);
      if (!pending) return;
      workerPending.delete(id);
      if (error) {
        pending.reject(new Error(error));
      } else {
        pending.resolve(result);
      }
    };
    decompressWorker.onerror = () => {
      // Reject everything in flight; callers fall back to the sync path.
      workerPending.forEach((pending) => pending.reject(new Error('Worker error')));
      workerPending.clear();
      workerUnavailable = true;
      decompressWorker = null;
    };
  } catch (e) {
    console.warn('Decompress worker unavailable, using main thread:', e);
    workerUnavailable = true;
    decompressWorker = null;
  }
  return decompressWorker;
};

const callWorker = (action, data) =>
  new Promise((resolve, reject) => {
    const worker = getDecompressWorker();
    if (!worker) {
      reject(new Error('Worker unavailable'));
      return;
    }
    const id = ++workerRequestId;
    workerPending.set(id, { resolve, reject });
    worker.postMessage({ id, action, data });
  });

/**
 * Decompress a gzip-base64 encoded response, preferring an off-main-thread
 * Web Worker and falling back to synchronous processing on any failure.
 * @param {string} compressedData - Base64 encoded gzipped data
 * @returns {Promise<any>} Decompressed and parsed JSON data
 */
const decompressGzipBase64 = async (compressedData) => {
  try {
    return await callWorker('gunzip', compressedData);
  } catch (e) {
    return decompressGzipBase64Sync(compressedData);
  }
};

// Sync gzip-to-base64 fallback for when the worker is unavailable.
const compressToBase64Sync = (obj) => {
  const compressed = pako.gzip(JSON.stringify(obj));
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < compressed.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, compressed.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

const processApiResponse = async (responseData) => {
  if (responseData && responseData.__compressed === true && responseData.__encoding === 'gzip-base64') {
    console.log(`📦 Received compressed response: ${responseData.__compressedSize} bytes → ${responseData.__originalSize} bytes`);
    const decompressed = await decompressGzipBase64(responseData.data);
    console.log('✅ Successfully decompressed response');
    return decompressed;
  }
  return responseData;
};

// Cache variables
let cachedTreeData = null;
let cacheTimestamp = null;
const CACHE_DURATION_MS = 5 * 60000; // 5 minutes cache (default)
const CACHE_DURATION_AGENT_MS = 10 * 1000; // 10 seconds cache for agents/super-agents
const LOCAL_STORAGE_KEY = 'userTreeServiceCache';
const LOCAL_STORAGE_TIMESTAMP_KEY = 'userTreeServiceCacheTimestamp';

// Large trees (6MB+ as JSON) exceed the ~5MB localStorage quota, so the old
// plain-JSON setItem silently failed for exactly the users who needed the cache
// most — nothing was ever persisted and every reload was a cold fetch. We now
// store the tree gzip-base64 compressed (~7x smaller) with a prefix marker.
const STORAGE_COMPRESSED_PREFIX = 'GZB64:';

/**
 * Persist the tree to localStorage compressed. Fire-and-forget; logs loudly on
 * failure instead of silently swallowing quota errors.
 */
const persistTreeToLocalStorage = (treeData, timestamp) => {
  (async () => {
    let payload;
    try {
      const base64 = await callWorker('gzip', treeData);
      payload = STORAGE_COMPRESSED_PREFIX + base64;
    } catch (e) {
      try {
        payload = STORAGE_COMPRESSED_PREFIX + compressToBase64Sync(treeData);
      } catch (syncErr) {
        console.warn('⚠️ Tree cache compression failed, not persisting:', syncErr);
        return;
      }
    }
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, payload);
      localStorage.setItem(LOCAL_STORAGE_TIMESTAMP_KEY, timestamp.toString());
      console.log(`💾 Persisted user tree cache (${(payload.length / 1024).toFixed(0)} KB compressed)`);
    } catch (e) {
      console.warn('⚠️ Failed to persist user tree cache (localStorage quota?):', e);
    }
  })();
};

/**
 * Parse a stored tree value: compressed (prefixed) or legacy plain JSON.
 * @returns {Promise<object>}
 */
const parseStoredTree = async (storedValue) => {
  if (storedValue.startsWith(STORAGE_COMPRESSED_PREFIX)) {
    return decompressGzipBase64(storedValue.slice(STORAGE_COMPRESSED_PREFIX.length));
  }
  return JSON.parse(storedValue);
};

const getCacheDuration = () => {
  if (cachedTreeData && (cachedTreeData.type === 'super-agent' || cachedTreeData.type === 'agent')) {
    return CACHE_DURATION_AGENT_MS;
  }
  return CACHE_DURATION_MS;
};

let fetchPromise = null;
let isRequestInProgress = false;
let requestCount = 0;

const balanceUpdateSubscribers = new Set();
const userUpdateSubscribers = new Set();
const treeRefreshSubscribers = new Set();

export const subscribeToBalanceUpdates = (callback) => {
  balanceUpdateSubscribers.add(callback);
  return () => balanceUpdateSubscribers.delete(callback);
};

export const subscribeToUserUpdates = (callback) => {
  userUpdateSubscribers.add(callback);
  return () => userUpdateSubscribers.delete(callback);
};

/** Fired when a background refresh completes after stale-while-revalidate (or when fresh data replaces stale). */
export const subscribeToTreeRefresh = (callback) => {
  treeRefreshSubscribers.add(callback);
  return () => treeRefreshSubscribers.delete(callback);
};

const notifyTreeRefreshed = (treeData) => {
  treeRefreshSubscribers.forEach((callback) => {
    try {
      callback(treeData);
    } catch (error) {
      console.error('Error in tree refresh subscriber:', error);
    }
  });
};

export const clearCache = () => {
  console.log('🧹 Clearing userTreeService cache');
  cachedTreeData = null;
  cacheTimestamp = null;
  localStorage.removeItem(LOCAL_STORAGE_KEY);
  localStorage.removeItem(LOCAL_STORAGE_TIMESTAMP_KEY);
  isRequestInProgress = false;
  fetchPromise = null;
  balanceUpdateSubscribers.clear();
  userUpdateSubscribers.clear();
  treeRefreshSubscribers.clear();
  console.log('🧹 UserTreeService cache cleared');
};

const findUserInTree = (tree, username) => {
  if (!tree) return null;
  if (tree.username === username) return tree;
  if (!tree.children) return null;
  for (const child of tree.children) {
    const found = findUserInTree(child, username);
    if (found) return found;
  }
  return null;
};

const notifyBalanceUpdate = (username, balances) => {
  balanceUpdateSubscribers.forEach((callback) => {
    try {
      callback(username, balances);
    } catch (error) {
      console.error('Error in balance update subscriber:', error);
    }
  });
};

const notifyUserUpdate = (username, userData) => {
  userUpdateSubscribers.forEach((callback) => {
    try {
      callback(username, userData);
    } catch (error) {
      console.error('Error in user update subscriber:', error);
    }
  });
};

const compareAndNotifyChanges = (oldTree, newTree) => {
  if (!oldTree || !newTree) return;

  const processNode = (oldNode, newNode) => {
    if (!oldNode || !newNode) return;
    if (JSON.stringify(oldNode.balances) !== JSON.stringify(newNode.balances)) {
      notifyBalanceUpdate(newNode.username, newNode.balances);
    }
    if (JSON.stringify(oldNode.attributes) !== JSON.stringify(newNode.attributes) ||
        oldNode.type !== newNode.type ||
        oldNode.name !== newNode.name) {
      notifyUserUpdate(newNode.username, newNode);
    }
    if (oldNode.children && newNode.children) {
      oldNode.children.forEach((oldChild, index) => {
        const newChild = newNode.children[index];
        if (newChild) {
          processNode(oldChild, newChild);
        }
      });
    }
  };

  processNode(oldTree, newTree);
};

/** Client-side timing for correlating with Lambda Init Duration / duration in CloudWatch */
const logFetchPerf = (startedAt, currentRequestId, phase) => {
  if (startedAt == null) return;
  const ms = Date.now() - startedAt;
  console.log(`⏱️ userTree[${currentRequestId}] ${phase}: ${ms}ms (client)`);
};

/**
 * Snapshot of the in-memory tree (for synchronous reads). May be stale if TTL expired.
 * @returns {object|null}
 */
export const getCachedTree = () => cachedTreeData;

async function runNetworkFetch(currentRequestId, startedAt, path = '/core/usertree') {
  try {
    const result = await get({
      apiName: 'coreuser',
      path
    }).response;

    if (result.body === '') {
      throw new Error('Empty response from server');
    }

    const rawData = await result.body.json();
    const treeData = await processApiResponse(rawData);

    if (cachedTreeData) {
      compareAndNotifyChanges(cachedTreeData, treeData);
    }

    cachedTreeData = treeData;
    cacheTimestamp = Date.now();
    persistTreeToLocalStorage(cachedTreeData, cacheTimestamp);

    console.log(`🌳 [${currentRequestId}] Fresh data fetched and cached`);
    notifyTreeRefreshed(treeData);
    logFetchPerf(startedAt, currentRequestId, 'network');
    return treeData;
  } finally {
    isRequestInProgress = false;
  }
}

/**
 * Lightweight tree root payload (~fast Lambda path). Does not include descendants.
 * @returns {Promise<object>}
 */
export const fetchUserTreeSummary = async () => {
  requestCount++;
  const currentRequestId = requestCount;
  const startedAt = Date.now();
  try {
    const result = await get({
      apiName: 'coreuser',
      path: '/core/usertree?summary=1'
    }).response;
    if (result.body === '') {
      throw new Error('Empty summary response from server');
    }
    const rawData = await result.body.json();
    const data = await processApiResponse(rawData);
    logFetchPerf(startedAt, currentRequestId, 'summary');
    return data;
  } catch (error) {
    console.error(`🌳 [${currentRequestId}] Summary fetch failed:`, error);
    throw error;
  }
};

/**
 * @param {boolean} [forceRefresh=false] - Bypass cache and fetch from API
 * @param {{ staleWhileRevalidate?: boolean }} [options] - If true, return last known tree immediately when TTL expired, refresh in background
 * @returns {Promise<object>}
 */
export const fetchUserTree = async (forceRefresh = false, options = {}) => {
  const staleWhileRevalidate = options.staleWhileRevalidate === true;

  requestCount++;
  const currentRequestId = requestCount;
  const startedAt = Date.now();

  console.log(`🌳 [${currentRequestId}] Tree request started`, { forceRefresh, staleWhileRevalidate });

  try {
    if (forceRefresh) {
      cachedTreeData = null;
      cacheTimestamp = null;
    }

    const now = Date.now();

    if (!forceRefresh && (!cachedTreeData || !cacheTimestamp)) {
      const localCache = localStorage.getItem(LOCAL_STORAGE_KEY);
      const localTimestamp = localStorage.getItem(LOCAL_STORAGE_TIMESTAMP_KEY);
      if (localCache && localTimestamp) {
        try {
          const parsedCache = await parseStoredTree(localCache);
          const parsedTimestamp = parseInt(localTimestamp, 10);
          cachedTreeData = parsedCache;
          const cacheDuration = getCacheDuration();
          if (parsedCache && parsedTimestamp && (now - parsedTimestamp < cacheDuration)) {
            cacheTimestamp = parsedTimestamp;
            console.log(`🌳 [${currentRequestId}] Loaded user tree from localStorage (within TTL)`);
          } else {
            if (!staleWhileRevalidate || forceRefresh) {
              cachedTreeData = null;
              cacheTimestamp = null;
            } else {
              cacheTimestamp = parsedTimestamp;
              console.log(`🌳 [${currentRequestId}] Loaded stale tree from disk for SWR, age: ${now - parsedTimestamp}ms`);
            }
          }
        } catch (e) {
          localStorage.removeItem(LOCAL_STORAGE_KEY);
          localStorage.removeItem(LOCAL_STORAGE_TIMESTAMP_KEY);
          cachedTreeData = null;
          cacheTimestamp = null;
        }
      }
    }

    const cacheDuration = getCacheDuration();
    if (!forceRefresh && cachedTreeData && cacheTimestamp && (now - cacheTimestamp < cacheDuration)) {
      logFetchPerf(startedAt, currentRequestId, 'cache-hit');
      console.log(`🌳 [${currentRequestId}] Using cached user tree data`, {
        cacheAge: `${now - cacheTimestamp}ms`,
        cacheDuration: `${cacheDuration}ms`
      });
      return cachedTreeData;
    }

    if (!forceRefresh && staleWhileRevalidate && cachedTreeData && cacheTimestamp) {
      if (!isRequestInProgress) {
        isRequestInProgress = true;
        const previousTree = cachedTreeData;
        const bgStartedAt = Date.now();
        fetchPromise = runNetworkFetch(currentRequestId, bgStartedAt)
          .catch((err) => {
            console.error(`🌳 [${currentRequestId}] Background tree refresh failed:`, err);
            if (!isAuthError(err) && previousTree) {
              return previousTree;
            }
            throw err;
          })
          .finally(() => {
            fetchPromise = null;
          });
      }
      logFetchPerf(startedAt, currentRequestId, 'stale-return');
      console.log(`🌳 [${currentRequestId}] Returning stale tree while refresh runs in background`);
      return cachedTreeData;
    }

    if (isRequestInProgress && fetchPromise) {
      console.log(`🌳 [${currentRequestId}] Request already in progress, waiting...`);
      const result = await fetchPromise;
      logFetchPerf(startedAt, currentRequestId, 'await-inflight');
      return result;
    }

    isRequestInProgress = true;
    const netStartedAt = Date.now();
    fetchPromise = runNetworkFetch(currentRequestId, netStartedAt);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      fetchPromise = null;
    }
  } catch (error) {
    let errorDetails = {
      message: error.message || 'Unknown error',
      type: 'Unknown',
      source: 'Unknown',
      statusCode: error.response?.status || 500
    };

    if (error.response?.body) {
      try {
        const errorBody = typeof error.response.body === 'string'
          ? JSON.parse(error.response.body)
          : error.response.body;
        if (errorBody.errorDetails) {
          errorDetails = {
            ...errorDetails,
            type: errorBody.errorDetails.type || errorDetails.type,
            source: errorBody.errorDetails.source || errorDetails.source,
            message: errorBody.error || errorDetails.message,
            originalError: errorBody.errorDetails.originalError
          };
        }
      } catch (parseErr) {
        /* ignore */
      }
    }

    console.error(`🌳 [${currentRequestId}] Error fetching user tree:`, {
      errorType: errorDetails.type,
      source: errorDetails.source,
      statusCode: errorDetails.statusCode,
      message: errorDetails.message,
      originalError: errorDetails.originalError,
      fullError: error
    });

    if (errorDetails.statusCode === 429 || errorDetails.type.toLowerCase().includes('throttl')) {
      console.error(`🚦 Rate limiting detected: ${errorDetails.type} on ${errorDetails.source}`);
    }

    if (isAuthError(error)) {
      console.error('🔐 Authentication error detected in userTreeService');
      window.dispatchEvent(new CustomEvent('authError', {
        detail: { error: error.message || 'Authentication failed' }
      }));
      throw error;
    }

    if (cachedTreeData) {
      console.log(`🌳 [${currentRequestId}] Returning cached data after fetch error`);
      return cachedTreeData;
    }
    throw error;
  } finally {
    console.log(`🌳 [${currentRequestId}] Tree request completed`);
  }
};

export const getUserData = (username) => {
  if (!cachedTreeData) return null;
  if (username == null || username === '') {
    return cachedTreeData;
  }
  return findUserInTree(cachedTreeData, username);
};

const processUserTree = (data) => {
  const stats = {
    totalBalance: {},
    usersByType: {
      admin: 0,
      country: 0,
      city: 0,
      'super-agent': 0,
      agent: 0,
      player: 0
    },
    blockedUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    currencies: new Set(),
    totalAgentCount: 0,
    totalPlayerCount: 0
  };

  const processNode = (node) => {
    stats.usersByType[node.type] = (stats.usersByType[node.type] || 0) + 1;
    const isPlayer = node.type === 'player';
    const isAgent = ['country', 'city', 'super-agent', 'agent'].includes(node.type);

    if (isPlayer) {
      stats.totalPlayerCount++;
    } else if (isAgent) {
      stats.totalAgentCount++;
    }

    if (node.attributes?.currencies) {
      node.attributes.currencies.forEach((currency) => {
        stats.currencies.add(currency);
        stats.totalBalance[currency] = (stats.totalBalance[currency] || 0) + (node.balances[currency] || 0);
      });
    }

    const isBlocked = node.attributes?.blocked === true;
    if (isBlocked) {
      stats.blockedUsers++;
    }

    if (isPlayer) {
      const hasBalance = Object.values(node.balances || {}).some((balance) => balance > 0);
      if (hasBalance && !isBlocked) {
        stats.activeUsers++;
      } else {
        stats.inactiveUsers++;
      }
    }

    if (node.children && node.children.length > 0) {
      node.children.forEach(processNode);
    }
  };

  processNode(data);

  return {
    agentData: [
      { name: 'Country', value: stats.usersByType.country, color: '#3B82F6' },
      { name: 'City', value: stats.usersByType.city, color: '#10B981' },
      { name: 'Super Agents', value: stats.usersByType['super-agent'], color: '#F59E0B' },
      { name: 'Agents', value: stats.usersByType.agent, color: '#8B5CF6' }
    ].filter((item) => item.value > 0),

    userData: [
      { name: 'Active Players', value: stats.activeUsers, color: '#10B981' },
      { name: 'Inactive Players', value: stats.inactiveUsers, color: '#6B7280' },
      { name: 'Blocked Users', value: stats.blockedUsers, color: '#EF4444' }
    ].filter((item) => item.value > 0),

    totalUsers: stats.totalPlayerCount,
    totalAgents: stats.totalAgentCount,
    blockedUsers: stats.blockedUsers,
    currencies: Array.from(stats.currencies),
    balances: stats.totalBalance,

    metaData: {
      creationDate: data.creationDate,
      updatedAt: data.updatedAt,
      updatedBy: data.updatedBy
    }
  };
};

export const getUserTreeStats = async () => {
  try {
    const treeData = await fetchUserTree(false, { staleWhileRevalidate: true });
    return processUserTree(treeData);
  } catch (error) {
    console.error('Error processing user tree:', error);
    throw error;
  }
};

export default {
  getUserTreeStats,
  fetchUserTree,
  getCachedTree,
  fetchUserTreeSummary,
  subscribeToTreeRefresh
};
